/**
 * Browser-side decoder for BrakeAce / BYB TELEMETRY-V3 (.byb).
 * Logic mirrors `scripts/convert-byb-to-csv.mjs` (no Node fs).
 */

const MAGIC_BYTES = new TextEncoder().encode('TELEMETRY-V3');
const PRIMARY_KINDS = new Set([0xfe01, 0xfd01]);

const DEFAULT_GYRO = { mx: 0.008691681759940678, my: 0.008394306637262034, mz: 0.008766234513816926 };
const DEFAULT_GYRO_BIAS = { bx: -0.012961327590996335, by: 0.02320646665722675, bz: -0.0067190661252827565 };

const DEFAULT_ACCEL_BETA = {
  Ax: [-0.00011511234983216404, -0.000013631194964990084, -0.000006912722058753015, 0.000039490229181998056, -0.007014525238420814],
  Ay: [-0.0002540690083180552, -0.000050356107538215614, 0.00003327809360631335, -0.000033968010276519846, 0.6734797865227308],
  Az: [0.0002254177614695105, -0.00004225023774751159, 0.000016094044988181954, 0.00006680475064892346, -0.2249083968804511],
};

function indexOfMagic(u8) {
  const n = MAGIC_BYTES.length;
  outer: for (let i = 0; i <= u8.length - n; i += 1) {
    for (let j = 0; j < n; j += 1) {
      if (u8[i + j] !== MAGIC_BYTES[j]) continue outer;
    }
    return i;
  }
  return -1;
}

/**
 * The BYB app sometimes saves **CSV exports** with a `.byb` extension (semicolon-separated text),
 * not the binary TELEMETRY-V3 stream. Detect the first header row so we can parse as CSV.
 */
export function bybBufferLooksLikeAppCsvExport(arrayBuffer) {
  const u8 = new Uint8Array(arrayBuffer);
  let start = 0;
  if (u8.length >= 3 && u8[0] === 0xef && u8[1] === 0xbb && u8[2] === 0xbf) start = 3;
  const scanLen = Math.min(u8.length - start, 65536);
  if (scanLen < 12) return false;
  const head = new TextDecoder('utf-8', { fatal: false }).decode(u8.subarray(start, start + scanLen));
  const lines = head.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^acquisition\b/i.test(line) && line.length < 200) continue;
    const delim = line.includes(';') ? ';' : (line.includes(',') ? ',' : null);
    if (!delim) return false;
    const lower = line.toLowerCase();
    const firstCell = line.split(delim)[0]?.trim().toLowerCase() || '';
    const hasTime = firstCell === 'time' || /^time\b/i.test(lower);
    const hasSusp =
      lower.includes('fork') ||
      lower.includes('shock') ||
      lower.includes('susp1') ||
      lower.includes('susp2') ||
      lower.includes('front_disp') ||
      lower.includes('fbrake');
    return hasTime && hasSusp;
  }
  return false;
}

function findTelemetryStart(ab) {
  const u8 = ab instanceof Uint8Array ? ab : new Uint8Array(ab);
  const idx = indexOfMagic(u8);
  if (idx < 0) return null;
  const searchFrom = idx + MAGIC_BYTES.length;
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  for (let base = searchFrom; base < Math.min(u8.length, searchFrom + 4096); base += 1) {
    if (dv.getUint32(base, true) !== 1) continue;
    for (let gap = 48; gap <= 56; gap += 1) {
      if (base + gap + 4 > u8.length) break;
      if (dv.getUint32(base + gap, true) === 2) {
        return { dataStart: base, frameStride: gap };
      }
    }
  }
  return null;
}

function tryGpsPair(pdv, off) {
  if (off + 16 > pdv.byteLength) return null;
  const latE7 = pdv.getInt32(off, true);
  const lonE7 = pdv.getInt32(off + 4, true);
  if (latE7 <= -900_000_000 || latE7 >= 900_000_000) return null;
  if (lonE7 <= -1_800_000_000 || lonE7 >= 1_800_000_000) return null;
  const lat = latE7 * 1e-7;
  const lon = lonE7 * 1e-7;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  const altOff = off + 8;
  if (altOff + 8 > pdv.byteLength) return null;
  const alt = pdv.getFloat32(altOff, true);
  const gpsSpd = pdv.getFloat32(altOff + 4, true);
  if (!Number.isFinite(alt) || !Number.isFinite(gpsSpd)) return null;
  return { lat, lon, alt, gpsSpd, off };
}

function extractGpsFromPayload(pdv) {
  const primary = tryGpsPair(pdv, 28);
  if (primary) return primary;
  const lim = Math.min(pdv.byteLength - 16, 44);
  for (let off = 12; off <= lim; off += 1) {
    if (off === 28) continue;
    const hit = tryGpsPair(pdv, off);
    if (hit) return hit;
  }
  return null;
}

function finiteMinMax(vals) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity) return null;
  return { min, max };
}

function attachForkShockNormalized(rows) {
  const forkRange = finiteMinMax(rows.map((r) => r.fork));
  if (forkRange && forkRange.max !== forkRange.min) {
    const { min, max } = forkRange;
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (typeof r.fork === 'number') r.fork_normalized = (r.fork - min) / (max - min);
    }
  }
  const shockRange = finiteMinMax(rows.map((r) => r.shock));
  if (shockRange && shockRange.max !== shockRange.min) {
    const { min, max } = shockRange;
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (typeof r.shock === 'number') r.shock_normalized = (r.shock - min) / (max - min);
    }
  }
}

function decodeFrames(u8, dataStart, frameStride, rearOffset) {
  const rows = [];
  const dvMain = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  let offset = dataStart;
  let lastSeq = null;
  let timeAccumSec = null;

  let lastAccel = null;

  while (offset + frameStride <= u8.length) {
    const seq = dvMain.getUint32(offset, true);
    if (seq === 0 || seq > 2_000_000_000) break;

    const payloadOffset = offset + 4;
    const payloadLen = frameStride - 4;
    if (payloadLen < 44) break;

    const pdv = new DataView(u8.buffer, u8.byteOffset + payloadOffset, payloadLen);

    const kind = pdv.getUint16(0, true);
    const rawSuspFront = pdv.getUint16(0, true);
    const rawSuspRear = pdv.getUint16(2, true);

    const i0 = pdv.getInt16(16, true);
    const i1 = pdv.getInt16(18, true);
    const i2 = pdv.getInt16(20, true);
    const i3 = pdv.getInt16(22, true);
    const i4 = pdv.getInt16(24, true);
    const i5 = pdv.getInt16(26, true);

    const gps = extractGpsFromPayload(pdv);
    const brake1 = i0 / 2;
    const brake2 = i1 - rearOffset;

    const ig = [i2, i3, i4, i5];
    const predAx =
      DEFAULT_ACCEL_BETA.Ax[0] * ig[0] +
      DEFAULT_ACCEL_BETA.Ax[1] * ig[1] +
      DEFAULT_ACCEL_BETA.Ax[2] * ig[2] +
      DEFAULT_ACCEL_BETA.Ax[3] * ig[3] +
      DEFAULT_ACCEL_BETA.Ax[4];
    const predAy =
      DEFAULT_ACCEL_BETA.Ay[0] * ig[0] +
      DEFAULT_ACCEL_BETA.Ay[1] * ig[1] +
      DEFAULT_ACCEL_BETA.Ay[2] * ig[2] +
      DEFAULT_ACCEL_BETA.Ay[3] * ig[3] +
      DEFAULT_ACCEL_BETA.Ay[4];
    const predAz =
      DEFAULT_ACCEL_BETA.Az[0] * ig[0] +
      DEFAULT_ACCEL_BETA.Az[1] * ig[1] +
      DEFAULT_ACCEL_BETA.Az[2] * ig[2] +
      DEFAULT_ACCEL_BETA.Az[3] * ig[3] +
      DEFAULT_ACCEL_BETA.Az[4];

    if (PRIMARY_KINDS.has(kind)) {
      lastAccel = { Ax: predAx, Ay: predAy, Az: predAz };
    }
    const accel = lastAccel || { Ax: predAx, Ay: predAy, Az: predAz };

    const gx = DEFAULT_GYRO.mx * i3 + DEFAULT_GYRO_BIAS.bx;
    const gy = DEFAULT_GYRO.my * i4 + DEFAULT_GYRO_BIAS.by;
    const gz = DEFAULT_GYRO.mz * i5 + DEFAULT_GYRO_BIAS.bz;

    let timeSec;
    if (timeAccumSec == null) {
      timeAccumSec = 0.002;
      timeSec = timeAccumSec;
    } else if (seq !== lastSeq) {
      timeAccumSec += 0.001;
      timeSec = timeAccumSec;
    } else {
      timeSec = timeAccumSec;
    }
    lastSeq = seq;

    const fork = rawSuspFront / 4095;
    const shock = rawSuspRear / 4095;

    /** @type {Record<string, number>} */
    const row = {
      time: timeSec,
      fork,
      shock,
      front_disp: rawSuspFront,
      rear_disp: rawSuspRear,
      front_brake: brake1,
      rear_brake: brake2,
      accel_x: accel.Ax,
      accel_y: accel.Ay,
      accel_z: accel.Az,
      gyro_x: gx,
      gyro_y: gy,
      gyro_z: gz,
    };

    if (gps) {
      row.lat = gps.lat;
      row.lon = gps.lon;
      row.elevation = gps.alt;
      row.speed = gps.gpsSpd;
      row.gps_speed = gps.gpsSpd;
    }

    rows.push(row);
    offset += frameStride;
  }

  return rows;
}

/**
 * @param {ArrayBuffer} arrayBuffer
 * @param {{ rearOffset?: number }} [opts]
 * @returns {Array<Record<string, number>>}
 */
export function parseBybTelemetryV3(arrayBuffer, opts = {}) {
  const rearOffset = typeof opts.rearOffset === 'number' ? opts.rearOffset : 1724;
  const u8 = new Uint8Array(arrayBuffer);
  const meta = findTelemetryStart(arrayBuffer);
  if (!meta) {
    throw new Error(
      'Could not find TELEMETRY-V3 binary stream in this file. If this is a BYB **app export** (CSV), open it as CSV or re-export; the app sometimes uses the .byb extension for text.',
    );
  }
  const rows = decodeFrames(u8, meta.dataStart, meta.frameStride, rearOffset);
  attachForkShockNormalized(rows);
  return rows;
}
